/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { Table } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/quote-management/components/QuoteTemplatesListTable/QuoteTemplatesListTable.css';

type Column = {
  key: string;
  label: string;
};

export interface QuoteTemplatesListTableProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'loading' | 'children'> {
  rowData: QuoteTemplateRowData[];
  loading?: boolean;
  className?: string;
  emptyStateMessage?: VNode;
  showItemRange?: boolean;
  itemRangeMessage?: VNode;
  showPageSizePicker?: boolean;
  pageSizePickerMessage?: VNode;
  showPagination?: boolean;
  paginationMessage?: VNode;
}

export type QuoteTemplateRowData = {
  id: string;
  name: VNode;
  state: VNode;
  status: VNode;
  validUntil: VNode;
  minQuoteTotal: VNode;
  ordersPlaced: VNode;
  lastOrdered: VNode;
  actions: VNode;
  [key: string]: VNode | string | number | undefined;
};

export const QuoteTemplatesListTable: FunctionComponent<
  QuoteTemplatesListTableProps
> = ({
  rowData = [],
  loading = false,
  className,
  emptyStateMessage,
  showItemRange = true,
  itemRangeMessage,
  showPageSizePicker = true,
  pageSizePickerMessage,
  showPagination = true,
  paginationMessage,
  ...props
}) => {
  const dictionary = useText({
    name: 'QuoteManagement.QuoteTemplatesListTable.name',
    state: 'QuoteManagement.QuoteTemplatesListTable.state',
    status: 'QuoteManagement.QuoteTemplatesListTable.status',
    validUntil: 'QuoteManagement.QuoteTemplatesListTable.validUntil',
    minQuoteTotal: 'QuoteManagement.QuoteTemplatesListTable.minQuoteTotal',
    ordersPlaced: 'QuoteManagement.QuoteTemplatesListTable.ordersPlaced',
    lastOrdered: 'QuoteManagement.QuoteTemplatesListTable.lastOrdered',
    actions: 'QuoteManagement.QuoteTemplatesListTable.actions',
  });

  const columns: Column[] = [
    { key: 'name', label: dictionary.name },
    { key: 'state', label: dictionary.state },
    { key: 'status', label: dictionary.status },
    { key: 'validUntil', label: dictionary.validUntil },
    { key: 'minQuoteTotal', label: dictionary.minQuoteTotal },
    { key: 'ordersPlaced', label: dictionary.ordersPlaced },
    { key: 'lastOrdered', label: dictionary.lastOrdered },
    { key: 'actions', label: dictionary.actions },
  ];

  // Check if should show the table when loading or there is data
  const shouldShowTable = loading || rowData.length > 0;

  // Check if we should show empty state
  const shouldShowEmptyState =
    !loading && rowData.length === 0 && emptyStateMessage;

  // Show item range if requested and message is provided
  const shouldShowItemRange = showItemRange && itemRangeMessage;

  // Show page size picker if requested and message is provided
  const shouldShowPageSizePicker = showPageSizePicker && pageSizePickerMessage;

  // Show pagination if requested and message is provided
  const shouldShowPagination = showPagination && paginationMessage;

  // Show footer if any pagination element should be shown
  const shouldShowFooter =
    shouldShowItemRange || shouldShowPageSizePicker || shouldShowPagination;

  return (
    <div
      {...props}
      className={classes([
        'quote-management-quote-templates-list-table',
        className,
      ])}
    >
      {shouldShowTable && (
        <Table
          columns={columns}
          rowData={rowData}
          loading={loading}
          mobileLayout="none"
          className="quote-management-quote-templates-list-table__table"
        />
      )}
      {shouldShowEmptyState && (
        <div className="quote-templates-list-table__empty-state">
          {emptyStateMessage}
        </div>
      )}
      {shouldShowFooter && (
        <div className="quote-templates-list-table__footer">
          <div className="quote-templates-list-table__item-range">
            {shouldShowItemRange && itemRangeMessage}
          </div>
          <div className="quote-templates-list-table__pagination">
            {shouldShowPagination && paginationMessage}
          </div>
          <div className="quote-templates-list-table__page-size-picker">
            {shouldShowPageSizePicker && pageSizePickerMessage}
          </div>
        </div>
      )}
    </div>
  );
};
